import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { formatDateTimeJST } from '../../../utils/datetime';
import type { EventPlayguideSource } from '../../../api/playguides';

type Props = {
  open: boolean;
  sources: EventPlayguideSource[];
  loading: boolean;
  onClose: () => void;
  onConfirm: (sourceIds: number[]) => void | Promise<void>;
};

const isCoolingDown = (source: EventPlayguideSource) => {
  const availableAt = Date.parse(source.manualSyncAvailableAt || '');
  return Number.isFinite(availableAt) && availableAt > Date.now();
};

export default function PlayguideTicketSyncModal({
  open,
  sources,
  loading,
  onClose,
  onConfirm,
}: Props) {
  const [mode, setMode] = useState<'all' | 'selected'>('all');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;
    setMode('all');
    setSelectedIds(sources.filter((source) => !isCoolingDown(source)).map((source) => source.sourceId));
  }, [open, sources]);

  const coolingSources = useMemo(() => sources.filter(isCoolingDown), [sources]);
  const targetIds = sources.length === 1
    ? sources.map((source) => source.sourceId)
    : mode === 'all'
      ? sources.map((source) => source.sourceId)
      : selectedIds;
  const selectionBlocked = !targetIds.length || targetIds.some((sourceId) =>
    coolingSources.some((source) => source.sourceId === sourceId)
  );

  const footer = (
    <>
      <DsButton variant="secondary" onClick={onClose} disabled={loading}>キャンセル</DsButton>
      <DsButton
        loading={loading}
        disabled={selectionBlocked}
        onClick={() => onConfirm(targetIds)}
      >
        取得を開始
      </DsButton>
    </>
  );

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="券売状況取得"
      ariaLabel="券売状況取得"
      size="sm"
      footer={footer}
      dismissible={!loading}
    >
      <div className="playguide-sync-modal">
        {sources.length === 1 ? (
          <p>「{sources[0].siteName || sources[0].organizationLabel}」から券売状況を取得しますか？</p>
        ) : (
          <>
            <p>取得するチケットサイトを選択してください。</p>
            <fieldset className="playguide-sync-modal__mode">
              <legend>取得対象</legend>
              <label>
                <input
                  type="radio"
                  name="playguide-sync-mode"
                  value="all"
                  checked={mode === 'all'}
                  onChange={() => setMode('all')}
                />
                全サイトから取得
              </label>
              <label>
                <input
                  type="radio"
                  name="playguide-sync-mode"
                  value="selected"
                  checked={mode === 'selected'}
                  onChange={() => setMode('selected')}
                />
                サイトを指定して取得
              </label>
            </fieldset>
            {mode === 'selected' ? (
              <div className="playguide-sync-modal__sources">
                {sources.map((source) => {
                  const coolingDown = isCoolingDown(source);
                  return (
                    <label key={source.sourceId}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(source.sourceId)}
                        disabled={coolingDown}
                        onChange={(event) => setSelectedIds((current) => event.target.checked
                          ? [...new Set([...current, source.sourceId])]
                          : current.filter((sourceId) => sourceId !== source.sourceId))}
                      />
                      <span>
                        <strong>{source.siteName || `${source.guideCode} / ${source.organizationLabel}`}</strong>
                        <small>イベントID {source.externalEventId}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
        {coolingSources.length ? (
          <div className="playguide-sync-modal__cooldown" role="status">
            <strong>連続取得を防止しています</strong>
            {coolingSources.map((source) => (
              <span key={source.sourceId}>
                {source.siteName || source.organizationLabel}：{formatDateTimeJST(source.manualSyncAvailableAt) || 'しばらく後'}から取得できます。
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </DsModal>
  );
}
